import AnimatedLoader from "../../../motionKit/AnimatedLoader";
import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Accordion from "../../../components/shared/Accordion";
import EmailThread from "../../../components/shared/EmailThread";
import {
  fetchEmailThread,
  replyToEmailThread,
  registerWatchedThread,
  emailTemplatePopulated,
  getListOfTemplates,
  postQuoteMessageAssist,
} from "../../../Api";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import AiMessageAssistPopover from "../../../components/shared/AiMessageAssistPopover";
import { useParams } from "react-router-dom";
import { formatMissingFieldLabel } from "../../../Helper/FilterationFunctions";
import { formatTemplateOptionLabel } from "../../../Helper/emailDisplayUtils";
import TemplateSwitchConfirmModal from "../../../components/shared/TemplateSwitchConfirmModal";
import {
  clearHighlight,
  clearThreadMessages,
} from "../../../redux/threadSlice";
import ReactDOMServer from "react-dom/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faWandMagicSparkles, faCircleNotch, faRotateRight, faEnvelopeOpenText } from "@fortawesome/free-solid-svg-icons";
import { stripHtmlToText } from "../../../Helper/aiEmailParseHelpers";
import { sanitizeEmailHtml } from "../../../Helper/sanitizeHtml";
import { ShimmerBox } from "../../../components/shared/SkeletonComponents";
import { sortThreadMessages } from "./threadOrdering";

// Helper function to parse email addresses from headers
const parseEmailAddresses = (headerValue) => {
  if (!headerValue) return [];
  // Split by comma and clean up
  return headerValue
    .split(',')
    .map(addr => {
      const trimmed = addr.trim();
      // Extract email from "Name <email>" format or use as-is
      if (trimmed.includes("<")) {
        return trimmed.split("<")[1].replace(">", "").trim();
      }
      return trimmed;
    })
    .filter(addr => addr && addr.length > 0);
};

const parseEmailMessage = (message) => {
  const fromHeader = message.headers.from || "";
  const toHeader = message.headers.to || "";
  const ccHeader = message.headers.cc || "";
  const bccHeader = message.headers.bcc || "";

  return {
    sender: fromHeader,
    email: fromHeader.includes("<")
      ? fromHeader.split("<")[1].replace(">", "")
      : fromHeader,
    to: parseEmailAddresses(toHeader),
    cc: parseEmailAddresses(ccHeader),
    bcc: parseEmailAddresses(bccHeader),
    subject: message.headers.subject,
    date: new Date(parseInt(message.internalDate)).toLocaleString(),
    // NaN would fall through the old guard to a re-parsed locale string; keep
    // it explicitly unknown instead. See threadOrdering.js.
    internalDate: Number.isFinite(parseInt(message.internalDate, 10))
      ? parseInt(message.internalDate, 10)
      : null,
    html: message.body,
    messageId: message.headers.messageId,
    id: message.id,
    attachments: message.attachments || [],
  };
};

export default function CustomerConversation({
  threadId,
  openPanel,
  setOpenPanel,
  panelTriggerKey,
  disabled,
  TO_EMAIL,
  setTO_EMAIL,
  USER_EMAIL,
  defaultOpen,
  highlight,
  onFullscreen,
  selectedQuote,
  onViewDetails,
  selectedQuoteId,
  layoutVariant = "default",
  /** Incomplete-queries split pane: accordion body fills column height (fixed clamp otherwise) */
  fillParentHeight = false,
  /** All-quotes / incomplete-queries: hide accordion fullscreen (not offered from listings). */
  suppressFullscreen = false,
  /** Email listing: open quote detail and run AI parse on thread text */
  onParseWithAi,
  parseInFlight = false,
  setRefetchTrigger,
}) {
  const loginToken = useSelector((state) => state.reducer.userToken);
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const liveMessages = useSelector(
    (state) => state.threads.liveMessages?.[threadId] || []
  );
  const prevLiveLenRef = useRef(0);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const len = liveMessages.length;
    if (len > prevLiveLenRef.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 2800);
      prevLiveLenRef.current = len;
      return () => clearTimeout(t);
    }
    prevLiveLenRef.current = len;
  }, [liveMessages.length]);
  const dispatch = useDispatch();
  const [emailMessages, setEmailMessages] = useState([]);
  const [scrollOnUpdate, setScrollOnUpdate] = useState(false);
  const bottomRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [threadRefreshLoading, setThreadRefreshLoading] = useState(false);
  const [threadRefreshCooldown, setThreadRefreshCooldown] = useState(false);

  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateData, setTemplateData] = useState({});
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templatePopulating, setTemplatePopulating] = useState(false);
  const replyDraftDirtyRef = useRef(false);
  const onReplyDraftDirty = useCallback((dirty) => {
    replyDraftDirtyRef.current = dirty;
  }, []);
  const [templateSwitchModalOpen, setTemplateSwitchModalOpen] = useState(false);
  const [pendingTemplateName, setPendingTemplateName] = useState(null);
  const quoteId = useParams().id || selectedQuoteId;
  const isFlatLayout = layoutVariant === "flat";

  const showHeaderParseAi = Boolean(
    onParseWithAi &&
      selectedQuote &&
      selectedQuote?.type === "query" &&
      selectedQuote?.source !== "manual"
  );
  const isReParse = Boolean(selectedQuote?.aiParsedAt);

  const [isTable, setTable] = useState([]);
  /* The attached-table preview is collapsed by default. Sending a shortlist of
     options produces one table per option, and at full height they pushed the
     reply composer — and the Send Reply button with it — past the bottom of the
     panel with nothing able to scroll far enough to reach it. A summary line
     costs the operator one click and guarantees the composer is always
     reachable however many options are attached. */
  const [tablesExpanded, setTablesExpanded] = useState(false);

  // Shared sentinel used at BOTH the template emit site (fetchTemplateContent)
  // and the reply consume site (handleReplySubmit). Kept as a single const so
  // the two can never drift apart.
  const TABLE_MARKER = "[NON-EDITABLE TABLE ATTACHMENT SHOWN EXTERNALLY]";

  const [assistOpenForMessageId, setAssistOpenForMessageId] = useState(null);
  const [assistAnchorRect, setAssistAnchorRect] = useState(null);
  const [assistLoading, setAssistLoading] = useState(false);
  const [assistError, setAssistError] = useState(null);
  const [assistData, setAssistData] = useState({
    summary: null,
    suggestedReply: null,
    cached: false,
    generatedAt: null,
  });
  const [replyDraftInjection, setReplyDraftInjection] = useState({
    text: "",
    nonce: 0,
  });

  const threadHasText =
    (selectedQuote?.subject || "").trim().length > 0 ||
    (emailMessages || []).some((m) => stripHtmlToText(m.html || "").trim().length > 0);

  const closeAssist = useCallback(() => {
    setAssistOpenForMessageId(null);
    setAssistAnchorRect(null);
    setAssistError(null);
    setAssistLoading(false);
  }, []);

  const handleAssistRegenerate = useCallback(async () => {
    if (!assistOpenForMessageId) return;
    const qid = selectedQuote?._id || quoteId;
    if (!qid || !loginToken || !userLoggedIn?.role) return;
    setAssistLoading(true);
    setAssistError(null);
    try {
      const res = await postQuoteMessageAssist({
        loginToken,
        role: userLoggedIn.role,
        quoteId: qid,
        messageId: assistOpenForMessageId,
        mode: "both",
        force: true,
      });
      setAssistData({
        summary: res.summary ?? null,
        suggestedReply: res.suggestedReply ?? null,
        cached: Boolean(res.cached),
        generatedAt:
          res.generatedAt != null
            ? new Date(res.generatedAt).toLocaleString()
            : null,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many assist requests. Try again in about a minute.");
        setAssistError("Too many requests. Please wait a minute.");
      } else {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Assist failed";
        setAssistError(typeof msg === "string" ? msg : "Assist failed");
      }
    } finally {
      setAssistLoading(false);
    }
  }, [assistOpenForMessageId, selectedQuote?._id, quoteId, loginToken, userLoggedIn?.role]);

  const handleAssistCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  }, []);

  const handleUseAssistReply = useCallback(
    (text) => {
      setReplyDraftInjection({ text: text || "", nonce: Date.now() });
      closeAssist();
    },
    [closeAssist]
  );

  const handleMessageAssist = useCallback(
    async (messageId, anchorEl) => {
      const rect = anchorEl?.getBoundingClientRect?.();
      setAssistAnchorRect(
        rect
          ? {
              top: rect.top,
              left: rect.left,
              right: rect.right,
              bottom: rect.bottom,
              width: rect.width,
              height: rect.height,
            }
          : null
      );
      setAssistOpenForMessageId(messageId);
      setAssistError(null);
      setAssistData({
        summary: null,
        suggestedReply: null,
        cached: false,
        generatedAt: null,
      });
      const qid = selectedQuote?._id || quoteId;
      if (!qid || !loginToken || !userLoggedIn?.role) {
        setAssistError("Missing quote or session");
        return;
      }
      setAssistLoading(true);
      try {
        const res = await postQuoteMessageAssist({
          loginToken,
          role: userLoggedIn.role,
          quoteId: qid,
          messageId,
          mode: "both",
          force: false,
        });
        setAssistData({
          summary: res.summary ?? null,
          suggestedReply: res.suggestedReply ?? null,
          cached: Boolean(res.cached),
          generatedAt:
            res.generatedAt != null
              ? new Date(res.generatedAt).toLocaleString()
              : null,
        });
      } catch (err) {
        const status = err?.response?.status;
        if (status === 429) {
          toast.error("Too many assist requests. Try again in about a minute.");
          setAssistError("Too many requests. Please wait a minute.");
        } else {
          const msg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Assist failed";
          setAssistError(typeof msg === "string" ? msg : "Assist failed");
        }
      } finally {
        setAssistLoading(false);
      }
    },
    [selectedQuote?._id, quoteId, loginToken, userLoggedIn?.role]
  );

  useEffect(() => {
    closeAssist();
  }, [selectedQuote?._id, closeAssist]);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useLayoutEffect(() => {
    if (scrollOnUpdate && isOpen) {
      scrollToBottom();
      setScrollOnUpdate(false);
    }
  }, [emailMessages, scrollOnUpdate, isOpen]);

  const fetchAndSetThread = useCallback(
    async (opts) => {
      const quiet = Boolean(opts?.quiet);
      if (!threadId) return null;
      if (quiet) setThreadRefreshLoading(true);
      else setLoading(true);
      try {
        const res = await fetchEmailThread({
          token: loginToken,
          threadId,
          userEmail: USER_EMAIL,
          role: userLoggedIn?.role,
        });
        if (res?.messages?.length) {
          const parsed = res.messages.map(parseEmailMessage);
          const firstFrom = res.messages[0]?.headers?.from || "";
          setTO_EMAIL(
            firstFrom.includes("<")
              ? firstFrom.split("<")[1].split(">")[0]
              : firstFrom
          );
          // Note: previously this block also did
          //   setSelectedTemplateName("");
          //   setTemplateData({});
          // when the thread had messages. That reset raced with the
          // openPanel-driven template loader (applyForcedTemplate →
          // fetchTemplateContent → setTemplateData). When the thread
          // fetch resolved AFTER the template fetch — which is the
          // common case for "Send to Customer Conversation" because
          // panelTriggerKey++ triggers both flows at the same instant —
          // the reset wiped the freshly-applied template and the
          // composer stayed empty. The new [threadId] useEffect already
          // resets template state on quote navigation; any in-place
          // refresh should preserve whatever template the user just
          // attached (plus any typed draft, which EmailThread's
          // "thread+empty body → clear reply" effect would otherwise
          // erase via this same templateData reset).
          setScrollOnUpdate(true);
          setEmailMessages(parsed);
          return parsed;
        }
        if (quiet) {
          return [];
        }
        return null;
      } catch (e) {
        if (quiet) {
          throw e;
        }
        return null;
      } finally {
        if (quiet) setThreadRefreshLoading(false);
        else setLoading(false);
      }
    },
    [loginToken, threadId, USER_EMAIL, setTO_EMAIL, userLoggedIn?.role]
  );

  // Optimistic refresh when the COPILOT sends a reply for this quote — the
  // operator shouldn't have to refresh the conversation by hand.
  useEffect(() => {
    const onCopilotReplySent = (e) => {
      const sentQuoteId = e?.detail?.quoteId;
      const currentQuoteId = selectedQuote?._id || quoteId;
      if (!sentQuoteId || !currentQuoteId) return;
      if (String(sentQuoteId) !== String(currentQuoteId)) return;
      // Give Gmail a beat to register the sent message, then quiet-refetch.
      setTimeout(() => fetchAndSetThread({ quiet: true }), 1200);
    };
    window.addEventListener("fk:copilot-reply-sent", onCopilotReplySent);
    return () =>
      window.removeEventListener("fk:copilot-reply-sent", onCopilotReplySent);
  }, [selectedQuote?._id, quoteId, fetchAndSetThread]);

  const handleManualThreadRefresh = useCallback(async () => {
    if (!threadId) return;
    if (threadRefreshLoading || threadRefreshCooldown) return;
    try {
      await fetchAndSetThread({ quiet: true });
      setRefetchTrigger?.((p) => p + 1);
      toast.success("Thread refreshed");
      setThreadRefreshCooldown(true);
      setTimeout(() => setThreadRefreshCooldown(false), 3000);
    } catch (e) {
      console.error("Could not refresh email thread", e);
      const detail =
        e?.response?.data?.details ||
        e?.response?.data?.error ||
        e?.message ||
        "Unknown error";
      toast.error(`Could not refresh thread: ${detail}`);
    }
  }, [
    threadId,
    threadRefreshLoading,
    threadRefreshCooldown,
    fetchAndSetThread,
    setRefetchTrigger,
  ]);

  const handleReplySubmit = async (replyHtml, attachments = [], recipients = {}) => {
    const latest = emailMessages?.[0];
    const allReferences = emailMessages
      .map((msg) => msg.messageId)
      .filter(Boolean)
      .reverse()
      .join(" ");

    try {
      // Re-materialize the nominated cost table(s) from state, UNCONDITIONALLY.
      // Previously this was gated on the visible sentinel surviving in the Quill
      // body — but that sentinel is editable text the operator often deletes and
      // Quill mangles, so the table was silently dropped. And the old
      // Arr.splice(1, 0, isTable) spliced the whole array in as one element,
      // producing stray commas for multi-table. Build the joined HTML from state
      // and attach it whether or not the marker is still present.
      const attachedTablesHtml = (Array.isArray(isTable) ? isTable : [isTable])
        .filter((t) => typeof t === "string" && t.trim() !== "")
        .join("");
      if (attachedTablesHtml) {
        replyHtml = replyHtml.includes(TABLE_MARKER)
          ? replyHtml.split(TABLE_MARKER).join(attachedTablesHtml)
          : `${replyHtml}\n${attachedTablesHtml}`;
      }

      // Ensure 'to' is an array with at least one valid email
      let toRecipients = Array.isArray(recipients.to)
        ? recipients.to.filter(email => email && email.trim())
        : (recipients.to ? [recipients.to] : []);

      // Fallback to TO_EMAIL if no recipients provided
      if (toRecipients.length === 0) {
        if (TO_EMAIL) {
          toRecipients = [TO_EMAIL];
        } else {
          // Last resort: use the original sender from the first message
          const firstMessage = emailMessages[emailMessages.length - 1];
          if (firstMessage?.email) {
            toRecipients = [firstMessage.email];
          } else {
            throw new Error('At least one recipient is required');
          }
        }
      }

      const sent = await replyToEmailThread({
        token: loginToken,
        threadId,
        to: toRecipients,
        cc: Array.isArray(recipients?.cc) ? recipients.cc.filter(e => e && e.trim()) : [],
        bcc: Array.isArray(recipients?.bcc) ? recipients.bcc.filter(e => e && e.trim()) : [],
        subject: latest?.subject || null,
        body: replyHtml,
        from: USER_EMAIL,
        inReplyTo: latest?.messageId,
        references: allReferences,
        role: userLoggedIn?.role,
        attachments,
      });
      // Add the sent message to local state immediately (without triggering highlight)
      const now = Date.now();
      const sentMessage = {
        sender: USER_EMAIL,
        email: USER_EMAIL,
        subject: latest?.subject || null,
        date: new Date(now).toLocaleString(),
        internalDate: now, // Use timestamp for consistent sorting
        html: replyHtml,
        messageId: sent?.messageId || `temp-${now}`,
        attachments: attachments || [],
      };

      // Add to local state without triggering highlight (append to bottom)
      setEmailMessages(prev => [...prev, sentMessage]);
      // Auto-scroll disabled as latest messages are shown first

      setTable([]);
      setSelectedTemplateName("");
      setTemplateData({});
    } catch (err) {
      /* A failed send used to be indistinguishable from a successful one: the
         error went to the console and the thread carried on as if the customer
         had been replied to. Say so out loud. */
      console.error("❌ error sending reply", err);
      toast.error(
        err?.response?.data?.details ||
        err?.response?.data?.message ||
        err?.message ||
        "Reply not sent — please try again."
      );
    }
  };

  const handleTemplateChange = (event) => {
    const selectedName = event.target.value;
    if (selectedName === selectedTemplateName) return;
    if (replyDraftDirtyRef.current) {
      setPendingTemplateName(selectedName);
      setTemplateSwitchModalOpen(true);
      return;
    }
    setSelectedTemplateName(selectedName);
    const template = availableTemplates.find(
      (t) => t.templateName === selectedName
    );
    if (template) fetchTemplateContent(template);
  };

  const confirmPendingTemplateSwitch = () => {
    const name = pendingTemplateName;
    setPendingTemplateName(null);
    setTemplateSwitchModalOpen(false);
    if (!name) return;
    setSelectedTemplateName(name);
    const template = availableTemplates.find((t) => t.templateName === name);
    if (template) fetchTemplateContent(template);
  };

  const cancelPendingTemplateSwitch = () => {
    setPendingTemplateName(null);
    setTemplateSwitchModalOpen(false);
  };

  const fetchTemplateContent = async (template) => {
    if (!template || !template.purpose || !template.for) return;

    setTemplatePopulating(true);
    try {
      const templateResponse = await emailTemplatePopulated({
        token: loginToken,
        role: userLoggedIn?.role,
        quoteId,
        purpose: template.purpose,
        for: template.for,
      });

      let responseEmail = templateResponse?.emailContent || {};

      if (
        openPanel?.customer?.missingFields?.length > 0 &&
        responseEmail?.message?.includes("[Missing_Fields]")
      ) {
        const formattedMissingFields = openPanel.customer.missingFields.map((field) =>
          formatMissingFieldLabel(field)
        );
        const missingFieldsHtmlList = `
          <p><strong>Missing Fields:</strong></p>
          <ul>${formattedMissingFields.map((f) => `<li>${f}</li>`).join("")}</ul>
        `.replace(/<li> - /g, "<li>");
        const parts = responseEmail.message.split("[Missing_Fields]");
        responseEmail.message = `${parts[0]}${missingFieldsHtmlList}${parts[1] ?? ""}`;
      }

      // if (responseEmail?.message?.includes("[Table]")) {
      //   const parts = responseEmail.message.split("[Table]");
      //   const tableHtml =
      //     typeof openPanel.customer.missingFields === "string"
      //       ? openPanel.customer.missingFields
      //       : openPanel.customer.missingFields?.outerHTML || "";
      //   responseEmail.message = `${parts[0]}${tableHtml}${parts[1] ?? ""}`;
      // }

      // Derive the nominated cost-table HTML from the source ("Send to Customer
      // Conversation" stashes it in openPanel.customer.missingFields). Normalise
      // array | single value | nullish without wrapping a missing/empty value in
      // a one-element array (which made the old code push `[].outerHTML` →
      // `undefined` → an empty attachment card that survived the send-reset
      // because the wrapper gates on `isTable.length > 0`, not on actual content).
      const rawSource = openPanel?.customer?.missingFields;
      const sourceArr = Array.isArray(rawSource)
        ? rawSource
        : rawSource != null
          ? [rawSource]
          : [];
      // Convert each entry to its HTML string up front and discard anything
      // we can't render (plain field-label strings for the Missing Fields list
      // are NOT table HTML — only accept strings that look like markup).
      const nominatedTableHtmlStrings = sourceArr
        .map((x) => {
          if (typeof x === "string") {
            const t = x.trim();
            return /<[a-z][\s\S]*>/i.test(t) ? t : "";
          }
          if (x instanceof HTMLElement) return (x.outerHTML || "").trim();
          return "";
        })
        .filter(Boolean);

      // Populate the table state from the nominated source REGARDLESS of whether
      // the chosen template authored a [Table] token. Many templates don't, so
      // the nominated table would otherwise never attach. handleReplySubmit
      // appends this state unconditionally at send time.
      if (nominatedTableHtmlStrings.length > 0) {
        setTable((prev) => {
          const merged = [...prev];
          for (const html of nominatedTableHtmlStrings) {
            if (!merged.includes(html)) merged.push(html);
          }
          return merged;
        });
      }

      // If the template DID author a [Table] token, swap it for the shared
      // marker so the visible body still shows where the (externally-rendered)
      // table sits. When it didn't, we leave the body alone — the table still
      // attaches via handleReplySubmit's unconditional append.
      if (responseEmail?.message?.includes("[Table]")) {
        const parts = responseEmail.message.split("[Table]");
        responseEmail.message = `${parts[0]} ${TABLE_MARKER} ${parts[1] ?? ""}`;
      }
      setTemplateData(responseEmail);
    } catch (err) {
      console.error("Error fetching template content:", err);
    } finally {
      setTemplatePopulating(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!loginToken || !threadId) return;
      dispatch(clearThreadMessages({ threadId }));
      await fetchAndSetThread();
      try {
        await registerWatchedThread({ token: loginToken, threadId, role: userLoggedIn?.role });
      } catch { }
    };
    init();
  }, [loginToken, threadId, fetchAndSetThread]);

  // Clear the reply composer's attached tables / forced template when the
  // user navigates to a different quote (threadId changes). Without this,
  // CustomerConversation re-uses the same React instance across quotes and
  // local state (isTable, templateData) carries over — the prior quote's
  // "NON-EDITABLE TABLE ATTACHMENTS HERE:" block stays glued to the new
  // quote's composer. Matches the post-send reset at the bottom of
  // sendReply (setTable([]) / setTemplateData({}) / setSelectedTemplateName("")).
  useEffect(() => {
    setTable([]);
    setTemplateData({});
    setSelectedTemplateName("");
  }, [threadId]);

  // A newly attached set starts collapsed too — otherwise attaching four
  // options immediately buries the composer again.
  useEffect(() => {
    setTablesExpanded(false);
  }, [isTable.length]);

  useEffect(() => {
    const fetchData = async () => {
      let X = ["NS:", "QS:"]
      let pattern = X.reduce((acc, x, i) => acc + (i > 0 ? "|" : "") + x, "");
      // let regex = new RegExp(`\\b(${pattern})`, "i");
      try {
        const AllTemplates = await getListOfTemplates({
          token: loginToken,
          role: userLoggedIn?.role,
          search: pattern,
          searchOnField: "templateName",
          for: "customer",
        });
        const filteredTemplates = AllTemplates?.emailTemplates || [];
        setAvailableTemplates(filteredTemplates);
        if (openPanel?.customer?.selectTemplate) {
          applyForcedTemplate(
            openPanel.customer.selectTemplate,
            filteredTemplates
          );
        }
      } catch {
      } finally {
        setTemplateLoading(false);
      }
    };
    if (loginToken && quoteId) fetchData();
  }, [loginToken, quoteId, panelTriggerKey]);

  const applyForcedTemplate = (templatePattern, templates) => {
    if (!templatePattern || !Array.isArray(templates) || templates.length === 0) {
      return;
    }
    const wanted = String(templatePattern).toLowerCase().trim();

    // Preferred: exact purpose match (case-insensitive, whitespace-trimmed).
    let template = templates.find(
      (t) => String(t.purpose || "").toLowerCase().trim() === wanted
    );

    // Fallback for when the DB row's `purpose` is blank, capitalised, or
    // slightly worded differently from the agreed string (e.g. user typed
    // "Nomination Confirmation" instead of "nomination_confirmation"). The
    // template name carries the same intent in human-readable form, so
    // match against it after underscores are converted to spaces.
    if (!template) {
      const readable = wanted.replace(/_/g, " ");
      template = templates.find((t) =>
        String(t.templateName || "").toLowerCase().includes(readable)
      );
    }

    if (template) {
      setSelectedTemplateName(template.templateName);
      fetchTemplateContent(template);
      return;
    }

    // No matching tenant template (name lacks the NS:/QS: prefix, or purpose
    // differs) — previously this silently did NOTHING and the composer stayed
    // empty after "Request Missing". Fall back to a built-in draft so the
    // operator always gets a pre-filled reply to review.
    if (wanted === "data_request") {
      const mf = openPanel?.customer?.missingFields;
      const list = (Array.isArray(mf) ? mf : [])
        .filter((x) => typeof x === "string")
        .map((f) => formatMissingFieldLabel(f));
      if (list.length > 0) {
        setTemplateData({
          subject: "",
          message: `<p>Hello,</p><p>To proceed with your quote, could you please share the following details:</p><ul>${list
            .map((f) => `<li>${f}</li>`)
            .join("")}</ul><p>Kind regards,<br/>[Your Company Name]</p>`,
        });
      }
    }
  };

  useEffect(() => {
    // availableTemplates must be in deps: when triggerPanel fires
    // (panelTriggerKey++) BEFORE the template list has finished loading,
    // applyForcedTemplate's .find() against an empty array returns
    // undefined and the composer stays empty. Re-running once templates
    // arrive makes the auto-fill robust against the race.
    if (
      openPanel?.customer?.selectTemplate &&
      Array.isArray(availableTemplates) &&
      availableTemplates.length > 0
    ) {
      applyForcedTemplate(
        openPanel.customer.selectTemplate,
        availableTemplates
      );
    }
  }, [openPanel, panelTriggerKey, availableTemplates]);

  useEffect(() => {
    if (highlight) {
      const timer = setTimeout(() => {
        dispatch(clearHighlight());
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [highlight, dispatch]);

  const combinedMessages = sortThreadMessages([...emailMessages, ...liveMessages]);

  function createMarkup(data) {
    // Quote/email table HTML is attacker-controllable (monitored inboxes), so
    // sanitize before it reaches dangerouslySetInnerHTML.
    return { __html: sanitizeEmailHtml(data) };
  }

  const templateComposerAddon = (
    <div className={`relative min-w-0 max-w-full ${isFlatLayout ? "space-y-2" : "space-y-1.5"}`}>
      {templatePopulating ? (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-sm dark:bg-[oklch(0.225_0.026_264/0.75)]"
          aria-busy="true"
          aria-label="Loading template"
        >
          <div className="flex flex-col items-center gap-2">
            <AnimatedLoader variant="docbuild" size={72} label="Loading template" />
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Loading template…</span>
          </div>
        </div>
      ) : null}
      <label
        htmlFor="template-select"
        className={`block transition-colors duration-300 ${
          isFlatLayout
            ? fillParentHeight
              ? "text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500"
              : "text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500"
            : "text-sm font-bold text-gray-700 dark:text-gray-200"
        }`}
      >
        {isFlatLayout ? "Email Template" : "Select Email Template:"}
      </label>
      <select
        id="template-select"
        className={`w-full transition-colors duration-300 ${
          isFlatLayout
            ? fillParentHeight
              ? "rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-[13px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-fk-accent/15 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200"
              : "rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-fk-accent/15 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200"
            : "rounded border border-gray-300 bg-white px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none focus:shadow-outline dark:border-[oklch(0.46_0.02_264)] dark:bg-[oklch(0.38_0.022_264)] dark:text-gray-200"
        }`}
        value={selectedTemplateName}
        onChange={handleTemplateChange}
        disabled={
          templateLoading || templatePopulating || availableTemplates.length === 0
        }
      >
        {templateLoading ? (
          <option value="" className="bg-white text-gray-700 dark:bg-[oklch(0.38_0.022_264)] dark:text-gray-200">
            Loading templates...
          </option>
        ) : availableTemplates.length === 0 ? (
          <option value="" className="bg-white text-gray-700 dark:bg-[oklch(0.38_0.022_264)] dark:text-gray-200">
            No templates available
          </option>
        ) : (
          availableTemplates.map((template) => (
            <option
              key={template._id}
              value={template.templateName}
              className="bg-white text-gray-700 dark:bg-[oklch(0.38_0.022_264)] dark:text-gray-200"
            >
              {formatTemplateOptionLabel(template)}
            </option>
          ))
        )}
      </select>
    </div>
  );

  return (
    <>
      <TemplateSwitchConfirmModal
        open={templateSwitchModalOpen}
        onConfirm={confirmPendingTemplateSwitch}
        onCancel={cancelPendingTemplateSwitch}
      />
      {/* react-hot-toast Toaster lives at the app root (src/index.js) — keep one
          singleton instance so toasts queued before navigation get dismissed by
          the new page's Toaster. Mounting another here used to leave "Opening
          quote and parsing email with AI…" stuck on every subsequent route. */}
      <Accordion
      title="Customer Conversation"
      variant={isFlatLayout ? "embedded" : "light"}
      fillParentHeight={Boolean(isFlatLayout && fillParentHeight)}
      naturalBodyHeight={Boolean(isFlatLayout && !fillParentHeight)}
      embeddedCompact={Boolean(isFlatLayout && fillParentHeight)}
      newMessageHeaderFlash={pulse}
      headerAction={
        threadId || (isFlatLayout && onViewDetails) ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            {threadId ? (
              <button
                type="button"
                onClick={handleManualThreadRefresh}
                disabled={threadRefreshLoading || threadRefreshCooldown}
                title={
                  threadRefreshCooldown && !threadRefreshLoading
                    ? "Just refreshed"
                    : "Refresh thread"
                }
                aria-label="Refresh thread"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200/90 bg-slate-50/90 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.8)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
              >
                <FontAwesomeIcon
                  icon={threadRefreshLoading ? faCircleNotch : faRotateRight}
                  className={`text-[12px] ${threadRefreshLoading ? "animate-spin" : ""}`}
                />
              </button>
            ) : null}
            {isFlatLayout && onViewDetails ? (
              <>
            <motion.button
              type="button"
              aria-label="Open full quote detail"
              onClick={onViewDetails}
              initial="rest"
              whileHover="hover"
              variants={{ rest: {}, hover: {} }}
              className={`inline-flex items-center gap-1.5 rounded-lg bg-fk-accent font-medium text-white shadow-md transition-shadow max-lg:gap-1 max-[960px]:px-2.5 max-[960px]:text-[10px] hover:shadow-[0_10px_28px_-8px_rgba(0,163,255,0.45)] hover:brightness-110 ${
                fillParentHeight
                  ? "px-2.5 py-1.5 text-[11px] max-[1280px]:px-2.5 max-[1280px]:py-1.5 max-lg:text-[10px]"
                  : "rounded-xl px-3.5 py-2 text-[12px] max-[1280px]:px-3 max-[1280px]:py-1.5 max-lg:rounded-lg max-lg:text-[11px]"
              }`}
            >
              <motion.span
                variants={{ rest: { scale: 1 }, hover: { scale: 1.15 } }}
                transition={{ type: "spring", stiffness: 420, damping: 18 }}
                className="inline-flex"
              >
                <FontAwesomeIcon icon={faEye} className="text-[10px]" />
              </motion.span>
              Open Quote
            </motion.button>
            {showHeaderParseAi ? (
              <button
                type="button"
                title={
                  isReParse
                    ? "Re-run AI parse — re-extracts shipment fields from the email (overwrites empty fields only)"
                    : "Parse the first message in this thread with AI (or use AI assist on a specific message in the thread)"
                }
                disabled={!threadHasText || parseInFlight}
                onClick={() => {
                  if (parseInFlight) return;
                  onParseWithAi(selectedQuote, null);
                }}
                className={`group relative inline-flex items-center gap-1.5 overflow-hidden font-semibold text-white shadow-[0_6px_18px_-6px_rgba(124,58,237,0.6)] transition-all hover:shadow-[0_10px_24px_-6px_rgba(124,58,237,0.8)] hover:brightness-110 disabled:cursor-not-allowed disabled:bg-none disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none dark:disabled:bg-[oklch(0.3_0.024_264)] dark:disabled:text-slate-500 max-[960px]:px-2 max-[960px]:text-[10px] ${
                  isReParse
                    ? "rounded-lg bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-500"
                    : "rounded-lg bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_50%,#3b82f6_100%)]"
                } ${
                  fillParentHeight
                    ? "px-2.5 py-1.5 text-[11px]"
                    : "px-3 py-2 text-[12px]"
                }`}
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -translate-x-full bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] transition-transform duration-700 group-hover:translate-x-full group-disabled:hidden"
                />
                <span className="relative inline-flex items-center gap-1.5">
                  <span className="relative inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    <motion.span
                      aria-hidden
                      className="pointer-events-none absolute left-1/2 top-1/2 h-[11px] w-[11px]"
                      style={{ marginLeft: "-5.5px", marginTop: "-5.5px" }}
                      animate={
                        !threadHasText || parseInFlight
                          ? false
                          : { rotate: 360 }
                      }
                      transition={
                        !threadHasText || parseInFlight
                          ? {}
                          : { repeat: Infinity, duration: 5, ease: "linear" }
                      }
                    >
                      <span className="absolute left-1/2 top-0 h-[3px] w-[3px] -translate-x-1/2 rounded-full bg-white/95 shadow-[0_0_6px_rgba(255,255,255,0.85)]" />
                    </motion.span>
                    <motion.span
                      className="relative inline-flex h-3.5 w-3.5 items-center justify-center"
                      animate={
                        !threadHasText || parseInFlight
                          ? false
                          : { rotate: [0, -7, 0, 7, 0], scale: [1, 1.06, 1, 1.06, 1] }
                      }
                      transition={
                        !threadHasText || parseInFlight
                          ? {}
                          : { repeat: Infinity, duration: 4.5, ease: "easeInOut" }
                      }
                      whileHover={
                        !threadHasText || parseInFlight
                          ? undefined
                          : { rotate: [0, -14, 14, -10, 0], transition: { duration: 0.45 } }
                      }
                    >
                      <FontAwesomeIcon
                        icon={parseInFlight ? faCircleNotch : faWandMagicSparkles}
                        className={`text-[10px] ${parseInFlight ? "animate-spin" : ""}`}
                      />
                    </motion.span>
                  </span>
                  <span>
                    {parseInFlight
                      ? "Parsing…"
                      : isReParse
                        ? "Re-run AI Parse"
                        : "Parse w/ AI"}
                  </span>
                </span>
              </button>
            ) : null}
              </>
            ) : null}
          </div>
        ) : null
      }
      for="customer"
      defaultOpen={defaultOpen}
      openPanel={openPanel}
      setOpenPanel={setOpenPanel}
      panelTriggerKey={panelTriggerKey}
      disabled={disabled}
      setOpen={setIsOpen}
      highlight={highlight}
      onFullscreen={onFullscreen}
      showFullscreenButton={
        suppressFullscreen ? false : isFlatLayout ? true : !fillParentHeight
      }
    >
      <div
        className={`flex w-full min-w-0 max-w-full flex-col ${fillParentHeight && isFlatLayout ? "h-0 min-h-0 flex-1" : isFlatLayout ? "" : "h-full"} ${isFlatLayout ? "bg-white/50 dark:bg-[oklch(0.225_0.026_264/0.94)]" : ""}`}
      >
        {(() => {
          // Gate the wrapper on actual joined HTML, not on array length.
          // Empty / falsy / non-string entries (from older buggy paths or
          // race conditions) used to leave the bordered card visible with
          // no content after send — even though isTable looked "empty".
          const attachedTablesHtml = isTable
            .filter((t) => typeof t === "string" && t.trim() !== "")
            .join("");
          if (!attachedTablesHtml) return null;
          return (
            <div
              className={`flex-shrink-0 ${isFlatLayout ? "border-b border-slate-200/80 px-3 py-3 dark:border-[oklch(0.3_0.02_264/0.6)] sm:px-4 sm:py-3.5 xl:px-5 xl:py-4 max-[1280px]:px-2.5 max-[1280px]:py-2.5 max-lg:px-2 max-lg:py-2" : "mb-2 px-1"}`}
            >
              <div
                className={
                  isFlatLayout
                    ? "mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] max-[1280px]:mt-3 max-[1280px]:p-3 max-lg:rounded-xl max-lg:p-2.5"
                    : "rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)]"
                }
              >
                <div
                  className={`mb-2 flex items-center justify-between gap-2 ${isFlatLayout ? "text-left text-sm font-semibold text-slate-700 dark:text-slate-200" : "text-center text-lg font-extrabold"}`}
                >
                  <span>
                    {isTable.filter((t) => typeof t === "string" && t.trim() !== "").length}
                    {" "}
                    {isTable.filter((t) => typeof t === "string" && t.trim() !== "").length === 1
                      ? "table attached"
                      : "tables attached"}
                  </span>
                  {/* Let the operator remove a table attached by mistake before
                      sending — previously the card could not be cleared. */}
                  <div className="flex flex-shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setTablesExpanded((v) => !v)}
                    aria-expanded={tablesExpanded}
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100 dark:border-[oklch(0.4_0.02_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
                  >
                    {tablesExpanded ? "Hide" : "Preview"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTable([])}
                    aria-label="Remove attached table"
                    title="Remove attached table"
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-rose-600 dark:border-[oklch(0.4_0.02_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
                  >
                    <span className="text-[13px] leading-none">×</span>
                    Remove
                  </button>
                  </div>
                </div>
                {/* Cap the embedded table preview height + scroll within itself
                    so a tall cost table can't push the EmailThread's reply
                    composer (and the Send Reply CTA) below the visible panel
                    edge. */}
                {tablesExpanded ? (
                  <div
                    className="max-h-[30vh] overflow-y-auto overflow-x-auto"
                    dangerouslySetInnerHTML={createMarkup(attachedTablesHtml)}
                  />
                ) : (
                  <p className="text-[11.5px] leading-snug text-slate-500 dark:text-slate-400">
                    Sent with your reply. The customer sees the rate and total only.
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Email thread fills remaining space — bottomRef only inside EmailThread (end of message list) */}
        <div
          className={
            fillParentHeight && isFlatLayout
              ? "flex h-0 min-h-0 w-full max-w-full min-w-0 flex-1 flex-col overflow-hidden"
              : isFlatLayout
                ? "flex w-full min-w-0 max-w-full flex-col"
                : "flex h-full min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-x-hidden overflow-y-auto"
          }
        >
          {loading ? (
            <div className={`flex flex-col gap-3 py-6 ${isFlatLayout ? "px-5" : "px-4"}`}>
              <ShimmerBox width="45%" height={14} className="mb-1" />
              <ShimmerBox width="100%" height={72} borderRadius={14} />
              <ShimmerBox width="90%" height={14} />
              <ShimmerBox width="70%" height={14} />
              <ShimmerBox width="100%" height={88} borderRadius={14} />
            </div>
          ) : (
            <>
              {!loading && threadId && combinedMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 border-b border-slate-200/80 bg-slate-50/40 px-4 py-8 text-center dark:border-[oklch(0.3_0.02_264/0.6)] dark:bg-[oklch(0.225_0.026_264/0.25)]">
                  <FontAwesomeIcon
                    icon={faEnvelopeOpenText}
                    className="text-2xl text-slate-400 dark:text-slate-500"
                    aria-hidden
                  />
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    No messages in this thread yet
                  </p>
                  <p className="max-w-sm text-xs text-slate-500 dark:text-slate-400">
                    Tap refresh in the header to load from the server, or wait for a new email.
                  </p>
                </div>
              ) : null}
              <EmailThread
              messages={combinedMessages}
              onReplySubmit={handleReplySubmit}
              templateData={templateData}
              USER_EMAIL={USER_EMAIL}
              quoteId={selectedQuote?._id || quoteId}
              order="asc"
              bottomRef={bottomRef}
              variant={isFlatLayout ? "flat" : "default"}
              roomyComposer={Boolean(isFlatLayout && fillParentHeight)}
              naturalStackHeight={Boolean(isFlatLayout && !fillParentHeight)}
              composerAddon={templateComposerAddon}
              conversationScrollKey={threadId}
              onReplyDraftDirtyChange={onReplyDraftDirty}
              collapsibleComposer
              composerTourId="qd-reply-composer"
              listingCompact={Boolean(isFlatLayout && fillParentHeight)}
              onMessageAssist={loginToken ? handleMessageAssist : undefined}
              messageAssistInFlight={assistLoading}
              replyDraftInjection={replyDraftInjection}
            />
            </>
          )}
        </div>
      </div>
    </Accordion>
      <AiMessageAssistPopover
        open={assistOpenForMessageId != null}
        anchorRect={assistAnchorRect}
        loading={assistLoading}
        error={assistError}
        data={assistData}
        onClose={closeAssist}
        onRegenerate={handleAssistRegenerate}
        onUseReply={handleUseAssistReply}
        onCopy={handleAssistCopy}
      />
    </>
  );
}
